import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  eventContentSchema,
  expandOccurrences,
  repeats,
  type ContentBlob,
  type Event,
  type EventContent,
  type EventParticipantStatus,
} from "@vmeste/shared";
import { dateFormats, getLocale, t } from "../i18n/index.js";
import { operations } from "../lib/outbox.js";
import { selectEventParticipants, selectEvents } from "../lib/store.js";
import { syncEngine } from "../lib/sync.js";
import { plaintextOf } from "../lib/plaintext.js";

/**
 * Календарь (SPEC §4.4).
 *
 * Sibling of `wishes.tsx`: the same `useSyncExternalStore` on the sync engine,
 * the same codec boundary, the same outbox for writes. Nothing below this file
 * sees a ContentBlob.
 *
 * **No recurrence expansion.** `rrule` round-trips untouched and an event that
 * has one is marked as repeating, but SPEC §10 puts repeats in phase 6 proper,
 * so a weekly event shows once, at its `startsAt`. Pulling in an RRULE library
 * to render occurrences nobody can yet create or edit would be building the
 * hard half of the feature to serve none of it.
 *
 * **All-day is a display hint.** `startsAt` is never shifted or truncated for
 * it — the timestamp keeps whatever instant it was given, and `allDay` only
 * changes how the row is drawn. Everything here reads timestamps in the
 * device's local zone; there is no per-space timezone in v1.
 */

/*
 * The codec is the space's, asked for at each write (SPEC §3). `null` means an
 * encrypted space this device has no key for, and the event is not written:
 * plaintext in a space that is supposed to be encrypted would be a silent
 * failure, and the worst kind.
 */

/* ----------------------------------------------------------------- views */

/** What the screen renders. Content is already out of its blob. */
export interface EventView {
  id: string;
  /**
   * Unique per **occurrence**, not per event.
   *
   * A repeating event is one row in the database and many things on screen, and
   * every one of them needs its own React key. `id` alone would give a weekly
   * stand-up the same key on four different days of one month.
   */
  key: string;
  title: string;
  note?: string;
  location?: string;
  /** A link the event is about — a call, a ticket, a map. */
  url?: string;
  startsAt: Date;
  endsAt: Date | null;
  allDay: boolean;
  /**
   * Has a rule at all — not "has a rule we can expand".
   *
   * The badge answers a question about the row: does this event repeat? An
   * event carrying a rule outside our subset repeats; we simply cannot say when
   * beyond the first occurrence. Tying the badge to `repeats()` instead made
   * such an event look one-off in the list while the form said otherwise.
   */
  repeating: boolean;
  /** The rule as stored, so an edit can read it back without a second lookup. */
  rrule: string | null;
  reminderLeadMinutes: number | null;
  createdBy: string;
}

/**
 * Decode a plaintext blob synchronously.
 *
 * The same trick `lib/views.ts` plays for lists and wishes: `none` mode carries
 * plain JSON, so rendering stays synchronous. An e2ee blob cannot be read here
 * and the row falls back to a placeholder rather than vanishing off the screen.
 */
function decodeContent(blob: ContentBlob): EventContent | null {
  const value = plaintextOf(blob);
  if (value === undefined) return null;
  const parsed = eventContentSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function toEventView(event: Event): EventView {
  const content = decodeContent(event.content);

  return {
    id: event.id,
    key: event.id,
    title: content?.title ?? "…",
    ...(content?.note ? { note: content.note } : {}),
    ...(content?.location ? { location: content.location } : {}),
    ...(content?.url ? { url: content.url } : {}),
    startsAt: new Date(event.startsAt),
    endsAt: event.endsAt === null ? null : new Date(event.endsAt),
    allDay: event.allDay,
    repeating: event.rrule !== null,
    rrule: event.rrule,
    reminderLeadMinutes: event.reminderLeadMinutes,
    createdBy: event.createdBy,
  };
}

/* -------------------------------------------------------------- occurrences */

/**
 * Turn events into the things actually drawn, over a window.
 *
 * A repeating event is one row and many appearances, and the expansion has to
 * be bounded by *something*: «каждый день» has no last occurrence, so asking
 * for "all of them" is not a question with an answer. The screen names the
 * window it is about to draw — a month, or the stretch either side of today —
 * and gets exactly the occurrences inside it.
 *
 * The rule itself is read in `@vmeste/shared`, so the day a dot appears on here
 * and the occurrence the server reminds about are decided by the same code.
 */
export function expandInWindow(
  events: readonly EventView[],
  from: Date,
  to: Date,
): EventView[] {
  const out: EventView[] = [];

  for (const event of events) {
    for (const occurrence of expandOccurrences(event, from, to)) {
      out.push(
        // A rule we cannot expand yields exactly one occurrence, at the event's
        // own start — the old behaviour — and there is nothing to copy for it.
        occurrence.index === 0 && !repeats(event.rrule)
          ? event
          : {
              ...event,
              key: `${event.id}@${occurrence.startsAt.toISOString()}`,
              startsAt: occurrence.startsAt,
              endsAt: occurrence.endsAt,
            },
      );
    }
  }

  return out.sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
}

/* ------------------------------------------------------------- the draft */

/**
 * What the form holds, in the shape the native inputs speak: `date` is
 * `yyyy-mm-dd` and the times are `HH:mm`, both read as **local** wall time.
 *
 * `location` travelled through here before the form had a field for it: the
 * content blob is replaced wholesale on every edit, so carrying it through the
 * draft is what stopped an edit from quietly deleting one somebody else set.
 * The field exists now, and reads and writes the same string.
 */
export interface EventDraft {
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  note: string;
  location: string;
  /** A link, or empty. Validated on save — a half-typed address is not one. */
  url: string;
  /**
   * An RFC 5545 rule, or null for a one-off.
   *
   * Carried through the draft rather than left on the row, now that the form
   * can set it. It was deliberately absent before, so that editing the title of
   * a repeating event could not quietly un-repeat it — with a control on the
   * form that protection becomes the opposite problem, and a rule the form
   * never showed would be one nobody could turn off.
   */
  rrule: string | null;
  /**
   * Minutes of notice for this event alone, or null for "as usual".
   *
   * A notice belongs to the person (SPEC §4.7) and this does not take that
   * away — it overrides it for one event, because one number cannot serve both
   * a birthday and dinner at seven, and a household has no third kind.
   */
  reminderLeadMinutes: number | null;
}

interface ParsedDraft {
  content: EventContent;
  startsAt: string;
  endsAt: string | null;
}

/**
 * The reason a draft cannot be saved, in the language on screen, or null when
 * it can.
 *
 * The form shows this; the hook refuses the same drafts, so a bug in one does
 * not write a broken event through the other. The words are looked up here, at
 * the moment of the check, rather than held in a table above — a table built at
 * module scope would keep whichever language the app started in.
 */
export function draftError(draft: EventDraft): string | null {
  if (!draft.title.trim()) return t("calendar.errorNoTitle");

  const start = parseLocal(draft.date, draft.startTime);
  if (!start) return t("calendar.errorDate");

  if (!draft.allDay && !draft.startTime) return t("calendar.errorStartTime");

  if (!draft.allDay && draft.endTime) {
    const end = parseLocal(draft.date, draft.endTime);
    if (!end) return t("calendar.errorEndTime");
    if (end.getTime() <= start.getTime()) return t("calendar.errorEndsBeforeStart");
  }

  /*
   * A link, if there is one, has to be one.
   *
   * Refused here rather than silently dropped by the schema on save: somebody
   * who typed an address and pressed save would otherwise watch it vanish with
   * no explanation, and conclude the field does not work.
   */
  if (draft.url.trim() && !eventContentSchema.shape.url.safeParse(draft.url.trim()).success) {
    return t("calendar.errorUrl");
  }

  return null;
}

/** `yyyy-mm-dd` + `HH:mm` read as local wall time. */
function parseLocal(date: string, time: string): Date | null {
  const [year, month, day] = date.split("-").map(Number);
  if (year === undefined || month === undefined || day === undefined) return null;
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  const [hours = 0, minutes = 0] = time ? time.split(":").map(Number) : [];
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

  // Component-wise rather than `new Date("2026-08-24")`, which the spec parses
  // as UTC midnight and would land somebody's event on the day before.
  const parsed = new Date(year, month - 1, day, hours, minutes, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseDraft(draft: EventDraft): ParsedDraft | null {
  if (draftError(draft) !== null) return null;

  const start = parseLocal(draft.date, draft.startTime);
  if (!start) return null;

  const note = draft.note.trim();
  const location = draft.location.trim();
  const url = draft.url.trim();

  // All-day keeps whatever time-of-day the draft carries. Rewriting it to
  // midnight would be exactly the truncation this screen is not allowed to do —
  // and toggling "весь день" on and back off would have silently moved the
  // event.
  const end =
    draft.allDay || !draft.endTime ? null : parseLocal(draft.date, draft.endTime);

  return {
    content: {
      title: draft.title.trim(),
      ...(note ? { note } : {}),
      ...(location ? { location } : {}),
      ...(url ? { url } : {}),
    },
    startsAt: start.toISOString(),
    endsAt: end ? end.toISOString() : null,
  };
}

/** A blank draft for a given day, with the time rounded up to the next hour. */
export function draftForDate(date: Date, now: Date = new Date()): EventDraft {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1);

  return {
    title: "",
    date: dayKey(date),
    startTime: timeValue(next),
    endTime: "",
    allDay: false,
    note: "",
    location: "",
    url: "",
    rrule: null,
    reminderLeadMinutes: null,
  };
}

export function draftFromEvent(event: EventView): EventDraft {
  return {
    title: event.title,
    date: dayKey(event.startsAt),
    startTime: timeValue(event.startsAt),
    // An end on another day cannot be expressed by one date field, so it is not
    // offered back for editing rather than being silently moved onto the start
    // day. Saving such an event drops the end; the form says so.
    endTime:
      event.endsAt && sameDay(event.startsAt, event.endsAt)
        ? timeValue(event.endsAt)
        : "",
    allDay: event.allDay,
    note: event.note ?? "",
    location: event.location ?? "",
    url: event.url ?? "",
    rrule: event.rrule,
    reminderLeadMinutes: event.reminderLeadMinutes,
  };
}

/* ------------------------------------------------------------------ hook */

export interface CalendarActions {
  /** Every event in the space, soonest first — one entry per stored row. */
  events: EventView[];
  /**
   * The occurrences inside a window, repeats expanded.
   *
   * What the screen draws. See `expandInWindow` for why a window is required
   * rather than optional.
   */
  occurrencesIn(from: Date, to: Date): EventView[];
  /** Whether the first pull has landed, so an empty screen can be honest. */
  loaded: boolean;
  /**
   * Add, and answer with the event's id once the server has it.
   *
   * `null` when it never got there, or when the draft was refused before it was
   * ever queued. The id is the awkward part, and it is the same awkwardness the
   * chat composer already has: a file cannot be attached to an event the server
   * has not seen — it answers "not found", which the outbox is right to treat as
   * permanent — so anything that wants to attach has to wait for this rather
   * than fire alongside it. That is why creating an event could not take a file
   * while editing one could.
   *
   * The event itself still appears instantly; that is the pending overlay's job
   * and it does not wait for any of this.
   */
  add(draft: EventDraft): Promise<string | null>;
  update(eventId: string, draft: EventDraft): void;
  remove(eventId: string): void;
}

export function useCalendar(spaceId: string | null): CalendarActions {
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);

  const events = useMemo(
    () => (spaceId ? selectEvents(syncEngine.view(spaceId)).map(toEventView) : []),
    [spaceId, snapshot],
  );

  const loaded = useMemo(
    () => (spaceId ? syncEngine.view(spaceId).revision > 0 : false),
    [spaceId, snapshot],
  );

  const occurrencesIn = useCallback(
    (from: Date, to: Date) => expandInWindow(events, from, to),
    [events],
  );

  const add = useCallback(
    async (draft: EventDraft): Promise<string | null> => {
      if (!spaceId) return null;

      const parsed = parseDraft(draft);
      if (!parsed) return null;

      // Asked for before the await below, not after: a space switched while the
      // encode was out would otherwise decide how this event is written.
      const codec = syncEngine.codec(spaceId);
      if (!codec) return null;

      const content = await codec.encode(parsed.content);

      const operation = operations.createEvent(spaceId, {
        content,
        startsAt: parsed.startsAt,
        endsAt: parsed.endsAt,
        allDay: draft.allDay,
        ...(draft.rrule ? { rrule: draft.rrule } : {}),
        ...(draft.reminderLeadMinutes === null
          ? {}
          : { reminderLeadMinutes: draft.reminderLeadMinutes }),
      });

      syncEngine.mutate(operation);

      // Waited on here, not at the call site: the screen has already shown the
      // event from the pending overlay, and only a caller with a file to attach
      // cares when the row actually exists. Exactly what `chat.send` does.
      const outcome = await syncEngine.whenSettled(operation.id);

      return outcome === "sent" ? operation.body.id : null;
    },
    [spaceId],
  );

  const update = useCallback(
    (eventId: string, draft: EventDraft) => {
      if (!spaceId) return;

      const parsed = parseDraft(draft);
      if (!parsed) return;

      const codec = syncEngine.codec(spaceId);
      if (!codec) return;

      void codec.encode(parsed.content).then((content) => {
        syncEngine.mutate(
          operations.updateEvent(spaceId, eventId, {
            content,
            startsAt: parsed.startsAt,
            endsAt: parsed.endsAt,
            allDay: draft.allDay,
            // Sent explicitly, including as `null`. The form now shows the rule,
            // so leaving it out of the patch would make "не повторяется" a
            // choice that saves and then does nothing.
            rrule: draft.rrule,
            // Explicitly, `null` included: "как обычно" is a choice, and a
            // patch that left it out would make choosing it save nothing.
            reminderLeadMinutes: draft.reminderLeadMinutes,
          }),
        );
      });
    },
    [spaceId],
  );

  const remove = useCallback(
    (eventId: string) => {
      if (!spaceId) return;
      syncEngine.mutate(operations.deleteEvent(spaceId, eventId));
    },
    [spaceId],
  );

  return { events, occurrencesIn, loaded, add, update, remove };
}

/* ---------------------------------------------------------- participants */

/**
 * One person in an event, as the sheet renders them.
 *
 * Everyone in the space appears, answered or not — that is the whole point of
 * the screen: "кто идёт" is only a useful question if the person who has not
 * said anything is visible as such. A member with no row on the server is
 * `unanswered`, which is exactly what an explicit `unanswered` row means, so the
 * two are folded here and nothing downstream can tell them apart.
 */
export interface ParticipantView {
  userId: string;
  name: string;
  avatarUrl: string | null;
  status: EventParticipantStatus;
  /** True for the signed-in person — the only row with controls on it. */
  isSelf: boolean;
  /** True for whoever made the event. A label, not a permission. */
  isCreator: boolean;
}

export interface ParticipantActions {
  participants: ParticipantView[];
  /** Your own answer, folded from the row or its absence. */
  mine: EventParticipantStatus;
  /** How many said yes — what a row in the agenda has width to show. */
  goingCount: number;
  /**
   * Say whether **you** are coming.
   *
   * Takes no user, and that is deliberate all the way down: the operation
   * carries none, the route is `…/participants/me`, and the server reads the
   * person off the session. There is no code path here through which one member
   * could answer for the other (SPEC §4.4).
   */
  setMine(status: EventParticipantStatus): void;
}

const NO_PARTICIPANTS: ParticipantActions = {
  participants: [],
  mine: "unanswered",
  goingCount: 0,
  setMine: () => undefined,
};

/**
 * Who is in one event, and the one answer this device may write.
 *
 * A sibling of `useCalendar` rather than part of it: it is asked per event, by
 * the sheet that has one open, while `useCalendar` is about the whole month.
 * Both read the same synced state through the same engine, so an answer given on
 * the other device arrives here the way a moved event does.
 *
 * @param self The signed-in person, or null before the session has loaded.
 */
export function useEventParticipants(
  spaceId: string | null,
  eventId: string | null,
  self: string | null,
): ParticipantActions {
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);

  const read = useMemo(() => {
    if (!spaceId || !eventId) return NO_PARTICIPANTS;

    const state = syncEngine.view(spaceId);
    const rows = selectEventParticipants(state, eventId);
    const answers = new Map(rows.map((row) => [row.userId, row.status] as const));
    const createdBy = state.events[eventId]?.createdBy ?? null;

    const participants: ParticipantView[] = state.members.map((member) => ({
      userId: member.userId,
      name: member.user.displayName,
      avatarUrl: member.user.avatarUrl,
      // The absence of a row and an explicit `unanswered` mean the same thing.
      status: answers.get(member.userId) ?? "unanswered",
      isSelf: self !== null && member.userId === self,
      isCreator: createdBy !== null && member.userId === createdBy,
    }));

    return {
      participants,
      mine: (self ? answers.get(self) : undefined) ?? ("unanswered" as const),
      // Counted off `participants` rather than off `rows`, so somebody who has
      // left the space stops being counted as coming.
      goingCount: participants.filter((row) => row.status === "going").length,
      setMine: NO_PARTICIPANTS.setMine,
    };
    // `snapshot` is a dependency because this reads straight out of the store,
    // and the engine notifies on every change.
  }, [spaceId, eventId, self, snapshot]);

  const setMine = useCallback(
    (status: EventParticipantStatus) => {
      if (!spaceId || !eventId) return;
      syncEngine.mutate(operations.participate(spaceId, eventId, { status }));
    },
    [spaceId, eventId],
  );

  return { ...read, setMine };
}

/* ------------------------------------------------------------------ days */

const DAY_MS = 86_400_000;

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Component-wise: the same one line as millisecond arithmetic, and exact. */
export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

/** `yyyy-mm-dd` in local time — the key a day is grouped and compared by. */
export function dayKey(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** `HH:mm` in local time, which is what an `<input type="time">` wants. */
export function timeValue(date: Date): string {
  return `${`${date.getHours()}`.padStart(2, "0")}:${`${date.getMinutes()}`.padStart(2, "0")}`;
}

export function sameDay(a: Date, b: Date): boolean {
  return dayKey(a) === dayKey(b);
}

export function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

/** Whole days from one to the other, ignoring the time of day. */
export function daysBetween(from: Date, to: Date): number {
  // Rounded rather than truncated: a day is not guaranteed to be exactly
  // DAY_MS long, and a whole-day count must not come back one short.
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS);
}

/** Monday is 0. Weeks start on Monday here and in the month grid. */
function weekdayIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/**
 * Six Monday-first weeks covering the month, leading and trailing days
 * included.
 *
 * Always six rows: a grid that changes height between March and April makes the
 * whole screen jump under your thumb.
 */
export function monthGrid(month: Date): Date[] {
  const first = startOfMonth(month);
  const start = addDays(first, -weekdayIndex(first));
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/**
 * Does this event sit on this day?
 *
 * Its span from start to end, not an expansion of its rule: a three-day trip
 * marks three days, a weekly event marks only the day it actually starts on.
 */
export function coversDay(event: EventView, day: Date): boolean {
  const from = startOfDay(event.startsAt).getTime();
  const to = startOfDay(event.endsAt ?? event.startsAt).getTime();
  const at = startOfDay(day).getTime();
  return at >= from && at <= to;
}

export function eventsOnDay(events: readonly EventView[], day: Date): EventView[] {
  return events.filter((event) => coversDay(event, day)).sort(byTimeOfDay);
}

/** All-day entries lead the day; the rest follow in clock order. */
function byTimeOfDay(a: EventView, b: EventView): number {
  if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
  return a.startsAt.getTime() - b.startsAt.getTime();
}

export interface DayGroup {
  key: string;
  date: Date;
  events: EventView[];
}

/**
 * Group events by the day they start on.
 *
 * A multi-day event appears once, under its start, with its end spelled out in
 * the row — repeating it under every day it spans would push everything else
 * off a phone screen.
 */
export function groupByDay(events: readonly EventView[]): DayGroup[] {
  const groups = new Map<string, DayGroup>();

  for (const event of events) {
    const date = startOfDay(event.startsAt);
    const key = dayKey(date);
    const group = groups.get(key) ?? { key, date, events: [] };
    group.events.push(event);
    groups.set(key, group);
  }

  return [...groups.values()]
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map((group) => ({ ...group, events: [...group.events].sort(byTimeOfDay) }));
}

/* ------------------------------------------------------------ formatting */

/*
 * Everything a person reads goes through Intl, in the language on screen. Month
 * and weekday names are never spelled out in this file: the browser already
 * knows that "1 сентября" is genitive and "сентябрь 2026" is not.
 *
 * The formatters are asked for inside each function rather than built once up
 * here. A module-scope `Intl.DateTimeFormat` freezes whichever language the app
 * loaded in, and this screen has to follow a language that changes afterwards;
 * `dateFormats` caches per language, so asking every call is not a cost.
 */

export function formatTime(date: Date): string {
  return dateFormats(getLocale()).time.format(date);
}

/**
 * Month alone, then the year appended by hand.
 *
 * `{ month: "long", year: "numeric" }` gives "август 2026 г.", and the "г." is
 * noise in a heading. The month name still comes from Intl — this only skips
 * the abbreviation, and only Intl knows that a month with a day beside it
 * declines to "августа" while one on its own does not. English is built the
 * same way, so both languages get a heading of exactly month and year.
 */
export function formatMonthTitle(month: Date): string {
  return `${dateFormats(getLocale()).monthName.format(month)} ${month.getFullYear()}`;
}

export function formatDayShort(date: Date): string {
  return dateFormats(getLocale()).dayMonthShort.format(date);
}

/**
 * The heading over a day in the agenda: "сегодня · 24 августа".
 *
 * Named days beat weekday names for the two days a person is actually planning
 * around; everything further out gets its weekday, which is how people say it.
 */
export function formatDayLabel(
  date: Date,
  today: Date = new Date(),
): { primary: string; secondary: string } {
  const formats = dateFormats(getLocale());
  const offset = daysBetween(today, date);

  const primary =
    offset === 0
      ? t("calendar.today")
      : offset === 1
        ? t("calendar.tomorrow")
        : offset === -1
          ? t("calendar.yesterday")
          : formats.weekdayLong.format(date);

  // The year only when it is not this one: "24 августа" every day of the year
  // reads better than "24 августа 2026".
  const secondary =
    date.getFullYear() === today.getFullYear()
      ? formats.dayMonthLong.format(date)
      : `${formats.dayMonthLong.format(date)} ${date.getFullYear()}`;

  return { primary, secondary };
}

/** "весь день", "19:00", "19:00 – 21:30", or "19:00 → 26 авг., 11:00". */
export function formatEventTime(event: EventView): string {
  const formats = dateFormats(getLocale());

  if (event.allDay) {
    return event.endsAt && !sameDay(event.startsAt, event.endsAt)
      ? t("calendar.allDayUntil", { date: formats.dayMonthShort.format(event.endsAt) })
      : t("common.allDay");
  }

  const start = formats.time.format(event.startsAt);
  if (!event.endsAt) return start;

  return sameDay(event.startsAt, event.endsAt)
    ? `${start} – ${formats.time.format(event.endsAt)}`
    : `${start} → ${formats.dayMonthShort.format(event.endsAt)}, ${formats.time.format(event.endsAt)}`;
}
